'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { INTENT_SAME_AS_WEB } from '@/lib/react-native/services/react-native.intent'
import { Button } from '@/ui/buttons/button/Button.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { Loader } from '@/ui/loaders/loader/Loader'
import { Skin } from '@/ui/skin/Skin'

const LOADING_OPACITY = 'opacity-40'

type SearchInputButtonsProps = {
  buttonSize?: 'sm' | 'md'
  className?: string
  isLoading: boolean
  isSearchButtonDisabled: boolean
  onResetAction?: () => void
  onSearchAction: () => void
  resetAriaLabel?: string
  searchAriaLabel?: string
  shouldShowReset: boolean
  value: string
  skinTint?: 'body' | 'body-alt'
  instantSearch?: boolean
  hideSearchButton?: boolean
}

export function SearchInputButtons({
  buttonSize = 'sm',
  className,
  isLoading,
  isSearchButtonDisabled = false,
  onResetAction,
  onSearchAction,
  resetAriaLabel,
  searchAriaLabel,
  shouldShowReset,
  value,
  skinTint = 'body',
  instantSearch: isInstantSearch = false,
  hideSearchButton = false,
}: SearchInputButtonsProps): React.ReactElement {
  const { t } = useTranslation(['searchInput'])

  return (
    <div
      className={twMerge(
        'absolute right-1 m-auto flex flex-row items-center',
        (shouldShowReset || isLoading) && 'border-brand-2-200',
        className,
      )}
    >
      <Skin tint={skinTint} className="flex flex-row items-center">
        {/* Reset button with separator */}
        {shouldShowReset && !isLoading && (
          <div className="flex flex-row items-center">
            <Button
              className="border-transparent"
              iconLeft={<Icon.X />}
              intent={INTENT_SAME_AS_WEB}
              isRound
              onPress={onResetAction}
              size={buttonSize}
              type="button"
              variant="quaternary"
            >
              {resetAriaLabel ?? t('searchInput:buttons.ariaLabels.reset')}
            </Button>
            {!isInstantSearch && !hideSearchButton && (
              <div className={twMerge('mr-1 h-2 border-l-px border-current', LOADING_OPACITY)} />
            )}
          </div>
        )}
        {/* Loader for instant search */}
        {isInstantSearch && isLoading && (
          <span className="grid size-4 place-items-center text-primary">
            <Loader />
          </span>
        )}
        {/* Search button */}
        {!isInstantSearch && !hideSearchButton && (
          <Button
            className={twMerge('!border-0', isLoading && LOADING_OPACITY)} // to strengthen border control
            data-disabled={isSearchButtonDisabled}
            iconLeft={<Icon.Search />}
            isDisabled={isSearchButtonDisabled}
            isLoading={isLoading}
            isRound
            onPress={onSearchAction}
            size={buttonSize}
            type="button"
            variant={value || isLoading || isSearchButtonDisabled ? 'primary' : 'quaternary'}
          >
            {searchAriaLabel ?? t('searchInput:buttons.ariaLabels.search')}
          </Button>
        )}
      </Skin>
    </div>
  )
}
